/**
 * Job timezones keyed by the code stored on Jobs.Timezone.
 * Must stay identical to utils/dateAndTimeUtils.js in MarTrakAPI, which feeds emails and SMS.
 *
 * The codes are stable identifiers, not clock abbreviations: existing jobs were saved as MST
 * (Alberta work) and PST (BC work) back when those codes resolved to the US zones. Alberta and
 * British Columbia stopped observing daylight saving time in 2026, so MST and PST now resolve
 * to the Canadian offsets and the US zones moved to USMT and USPT. Changing a code's meaning
 * here fixes every existing job without touching data.
 *
 * Alberta and BC use the fixed-offset zones Etc/GMT+6 and Etc/GMT+7 instead of America/Edmonton
 * and America/Vancouver. The IANA names only give the right answer on a runtime whose bundled
 * tz data is 2026c or newer, and we cannot audit every server image and field-device browser.
 * The Etc zones are correct everywhere. Their sign is inverted by convention: Etc/GMT+6 is UTC-6.
 *
 * Both provinces followed the US daylight saving rules until their last spring-forward on
 * 2026-03-08, so timestamps recorded before then are converted with the matching US zone, whose
 * pre-2026 rules exist in every tz database. That keeps history reading as the clocks did at the
 * time while anything later uses the fixed offset. Always convert through zoneFor() for this.
 *
 * - value: IANA zone handed to date-fns-tz / Intl for current timestamps
 * - history: zone for instants before `until` (the province's 02:00 spring-forward, in UTC)
 * - label: shown wherever a time is described ("Time and Date Displayed in ...")
 * - detail: DST behaviour, appended to the label in the Job Details dropdown
 * - abbr: short suffix printed after a time, e.g. "13:00:00 (AB)"
 * - code: the stored key, repeated so a selected entry can be saved back
 */
export const timezoneMap = {
    MST: {
        value: 'Etc/GMT+6', label: 'Alberta Time', detail: 'UTC-6, no DST', abbr: 'AB', code: 'MST',
        history: { zone: 'America/Denver', until: '2026-03-08T09:00:00.000Z' }
    },
    PST: {
        value: 'Etc/GMT+7', label: 'BC Time', detail: 'UTC-7, no DST', abbr: 'BC', code: 'PST',
        history: { zone: 'America/Los_Angeles', until: '2026-03-08T10:00:00.000Z' }
    },
    SK: { value: 'America/Regina', label: 'Saskatchewan Time', detail: 'UTC-6, no DST', abbr: 'SK', code: 'SK' },
    USMT: { value: 'America/Denver', label: 'US Mountain Time', detail: 'observes DST', abbr: 'MT', code: 'USMT' },
    USPT: { value: 'America/Los_Angeles', label: 'US Pacific Time', detail: 'observes DST', abbr: 'PT', code: 'USPT' },
    CST: { value: 'America/Chicago', label: 'US Central Time', detail: 'observes DST', abbr: 'CT', code: 'CST' },
    EST: { value: 'America/New_York', label: 'US Eastern Time', detail: 'observes DST', abbr: 'ET', code: 'EST' },
    AKST: { value: 'America/Anchorage', label: 'Alaska Time', detail: 'observes DST', abbr: 'AKT', code: 'AKST' },
    HST: { value: 'Pacific/Honolulu', label: 'Hawaii Time', detail: 'no DST', abbr: 'HT', code: 'HST' }
};

/**
 * The zone a job gets when none is stored or the stored code is unknown. Marler's work is
 * mostly in Alberta.
 */
export const defaultTimezone = timezoneMap.MST;

/**
 * Looks up the map entry for a stored Jobs.Timezone code. An unknown or missing code falls back
 * to the default so a bad row degrades to the wrong label rather than a blank page.
 * @param {string | null | undefined} code
 * @returns {{value: string, label: string, detail: string, abbr: string, code: string}}
 */
export const resolveTimezone = (code) => {
    if (!code) return defaultTimezone;

    const entry = timezoneMap[code];
    if (entry) return entry;

    console.warn(`Unknown job timezone code "${code}", using ${defaultTimezone.label}`);
    return defaultTimezone;
};

const entriesByZone = Object.values(timezoneMap).reduce(
    (lookup, entry) => ({ ...lookup, [entry.value]: entry }),
    {}
);

/**
 * The IANA zone to convert a given instant with. Entries that carry `history` use the older zone
 * for instants before `until`, so a pass time from January 2026 still reads as the clock did
 * then. Later instants, entries without history, and zone strings not in the map use the current
 * zone unchanged.
 * @param {string | {value: string, history?: {zone: string, until: string}}} timezone a map entry, or the zone string consumers already hold
 * @param {Date | string | number | null | undefined} instant
 * @returns {string}
 */
export const zoneFor = (timezone, instant) => {
    const entry = typeof timezone === 'string' ? entriesByZone[timezone] : timezone;
    if (!entry) return timezone;
    if (!entry.history || instant === null || instant === undefined) return entry.value;

    const time = instant instanceof Date ? instant.getTime() : new Date(instant).getTime();
    if (Number.isNaN(time)) return entry.value;

    return time < Date.parse(entry.history.until) ? entry.history.zone : entry.value;
};

/**
 * react-select options for the Job Details dropdown, in map order so Alberta is first.
 */
export const timezoneOptions = Object.values(timezoneMap).map(({ code, label, detail }) => ({
    value: code,
    label: `${label} (${detail})`
}));
